/**
 * @description Modal de formulário para adicionar/editar pets usando ModernModal como base
 * @module PetFormModal
 * @since Outubro 2025
 */

import React from "react";
import {
  TextField,
  Grid,
  InputAdornment,
  Autocomplete,
  MenuItem,
  Typography,
  Box,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slider,
  Stack,
} from "@mui/material";
import {
  Abc as AbcIcon,
  Cake as CakeIcon,
  Category as CategoryIcon,
  Palette as PaletteIcon,
  Healing as HealingIcon,
  LocationOn as LocationIcon,
  MaleOutlined as MaleIcon,
  FemaleOutlined as FemaleIcon,
  Pets as PetIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import {
  VET_ANIMAL_SPECIES,
  VET_GENDER_STATUS,
  VET_ANIMAL_COLORS,
} from "@/types/VetTypes";
import { UFValueSet } from "@/utils/useValueSet";
import ModernModal from "./ModernModal";

// Interface para o formulário de pet
export interface IPetFormData {
  name: string;
  birthDate: Dayjs | null;
  color: { code: string; display: string } | null;
  state: { label: string; value: string } | null;
  city: string;
  species: { code: string; display: string } | null;
  breed?: { code: string; display: string } | null;
  sex: string;
  genderStatus: { code: string; display: string } | null;
}

interface PetFormModalProps {
  // Controle do modal
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IPetFormData) => void | Promise<void>;

  // Modo: add ou edit
  mode: "add" | "edit";

  // Dados do formulário
  formData: IPetFormData;
  onFormChange: (field: keyof IPetFormData, value: any) => void;

  // Estado de loading
  loading?: boolean;

  // Validação
  isValid?: boolean;

  // Foto do pet (controle externo)
  initialPhotoUrl?: string;
  photoPreviewUrl?: string | null;
  onPhotoChange?: (base64: string, previewUrl: string) => void;
  onPhotoRemove?: () => void;
}

/**
 * Modal de formulário para adicionar ou editar pets usando ModernModal como base
 */
const PetFormModal: React.FC<PetFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  mode,
  formData,
  onFormChange,
  loading = false,
  isValid = true,
  initialPhotoUrl,
  photoPreviewUrl,
  onPhotoChange,
  onPhotoRemove,
}) => {
  const isAddMode = mode === "add";
  const gradientColors: [string, string] = isAddMode
    ? ["#00A884", "#00C896"] // Verde para adicionar
    : ["#2196f3", "#1976d2"]; // Azul para editar

  // Estados de seleção/recorte da foto (internos ao modal)
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [cropOpen, setCropOpen] = React.useState(false);
  const [cropImageSrc, setCropImageSrc] = React.useState<string | null>(null);
  const [cropScale, setCropScale] = React.useState<number>(1);
  const [cropOffsetY, setCropOffsetY] = React.useState<number>(0);
  const [cropOffsetX, setCropOffsetX] = React.useState<number>(0);
  const cropSize = 220;

  const handleSelectPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleWebFileChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      // Feedback fica a cargo do chamador (toast no nível superior)
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCropImageSrc(result);
      setCropScale(1);
      setCropOffsetX(0);
      setCropOffsetY(0);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const generateCircularCroppedImage = async (
    imageSrc: string,
    scale: number,
    offsetY: number,
    offsetX: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const size = cropSize;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas context não disponível"));

          ctx.clearRect(0, 0, size, size);
          ctx.save();
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          const imgRatio = img.width / img.height;
          const baseWidth = imgRatio >= 1 ? size : size * imgRatio;
          const baseHeight = imgRatio >= 1 ? size / imgRatio : size;
          const drawWidth = baseWidth * scale;
          const drawHeight = baseHeight * scale;
          const dx = (size - drawWidth) / 2 + offsetX;
          const dy = (size - drawHeight) / 2 + offsetY;

          ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
          ctx.restore();

          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => reject(e as any);
      img.src = imageSrc;
    });
  };

  const handleConfirmCrop = async () => {
    if (!cropImageSrc) return;
    try {
      const croppedDataUrl = await generateCircularCroppedImage(
        cropImageSrc,
        cropScale,
        cropOffsetY,
        cropOffsetX
      );
      const base64 = croppedDataUrl.split(",")[1] || "";
      onPhotoChange?.(base64, croppedDataUrl);
      setCropOpen(false);
    } catch (e) {
      setCropOpen(false);
    }
  };

  const handleCancelCrop = () => {
    setCropOpen(false);
    setCropImageSrc(null);
    setCropScale(1);
    setCropOffsetX(0);
    setCropOffsetY(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <ModernModal
        open={open}
        onClose={onClose}
        title={isAddMode ? "Adicionar Novo Pet" : "Editar Pet"}
        subtitle={
          isAddMode
            ? "Preencha as informações do seu animal de estimação"
            : "Atualize as informações do pet"
        }
        icon={
          isAddMode ? (
            <AddIcon sx={{ fontSize: 48 }} />
          ) : (
            <EditIcon sx={{ fontSize: 48 }} />
          )
        }
        gradientColors={gradientColors}
        onConfirm={() => onSubmit(formData)}
        confirmButtonText={isAddMode ? "Adicionar Pet" : "Salvar Alterações"}
        confirmDisabled={!isValid}
        loading={loading}
        loadingText="Salvando..."
        maxWidth="md"
      >
        <Grid container spacing={3}>
          {/* Linha 0: Foto do Pet - Exibida apenas quando os handlers de foto estão disponíveis */}
          {onPhotoChange && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    overflow: "hidden",
                    bgcolor: "grey.100",
                    border: "1px solid",
                    borderColor: "grey.300",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {photoPreviewUrl || initialPhotoUrl ? (
                    <img
                      src={photoPreviewUrl || initialPhotoUrl || undefined}
                      alt="Foto do Pet"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <PetIcon sx={{ color: "grey.400", fontSize: 36 }} />
                  )}
                </Box>

                <div>
                  <Button variant="outlined" onClick={handleSelectPhoto}>
                    {photoPreviewUrl ? "Trocar foto" : "Adicionar foto do Pet"}
                  </Button>
                  {photoPreviewUrl && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => onPhotoRemove?.()}
                      sx={{ ml: 1 }}
                    >
                      Remover foto
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={handleWebFileChange}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Formatos aceitos: PNG ou JPG
                  </Typography>
                </div>
              </Box>
            </Grid>
          )}
          {/* Linha 1: Nome e Data de Nascimento */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome do Pet"
              value={formData.name}
              onChange={(e) => onFormChange("name", e.target.value)}
              required
              placeholder="Ex: Rex, Miau, Bob"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AbcIcon sx={{ color: "action.active", fontSize: 28 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              <DatePicker
                label="Data de Nascimento"
                value={formData.birthDate}
                onChange={(date) => onFormChange("birthDate", date)}
                format="DD/MM/YYYY"
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: false, // Garante que o campo não seja marcado como obrigatório
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CakeIcon sx={{ color: "action.active" }} />
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Linha 2: Espécie e Raça */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              options={VET_ANIMAL_SPECIES}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.display
              }
              value={formData.species}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  onFormChange(
                    "species",
                    newValue.trim()
                      ? {
                          code: newValue.toUpperCase(),
                          display: newValue,
                        }
                      : null
                  );
                } else {
                  onFormChange("species", newValue || null);
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input" && inputValue) {
                  onFormChange("species", {
                    code: inputValue.toUpperCase(),
                    display: inputValue,
                  });
                }
                if (reason === "clear") {
                  onFormChange("species", null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Espécie"
                  required
                  placeholder="Selecione ou digite..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <CategoryIcon sx={{ color: "action.active" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Raça"
              placeholder="Digite a raça do pet"
              value={formData.breed?.display || ""}
              onChange={(e) => {
                const breedText = e.target.value;
                if (!breedText.trim()) {
                  onFormChange("breed", null);
                } else {
                  onFormChange("breed", {
                    code: breedText.toUpperCase(),
                    display: breedText,
                  });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PetIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Linha 3: Sexo e Cor */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Sexo"
              value={formData.sex}
              onChange={(e) => onFormChange("sex", e.target.value)}
              required
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) {
                    return (
                      <Typography sx={{ color: "text.disabled" }}>
                        Selecione o sexo
                      </Typography>
                    );
                  }
                  return value === "male" ? "Macho" : "Fêmea";
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.sex === "male" ? (
                      <MaleIcon sx={{ color: "action.active" }} />
                    ) : formData.sex === "female" ? (
                      <FemaleIcon sx={{ color: "action.active" }} />
                    ) : (
                      <PetIcon sx={{ color: "action.active" }} />
                    )}
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="" disabled>
                Selecione o sexo
              </MenuItem>
              <MenuItem value="male">Macho</MenuItem>
              <MenuItem value="female">Fêmea</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={formData.color}
              onChange={(_, data) => onFormChange("color", data)}
              options={VET_ANIMAL_COLORS}
              getOptionLabel={(option) => option.display || ""}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cor"
                  placeholder="Selecione a cor"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <PaletteIcon sx={{ color: "action.active" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Linha 4: Status de Castração */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={VET_GENDER_STATUS}
              getOptionLabel={(option) => option.display}
              value={formData.genderStatus}
              onChange={(_, newValue) => {
                onFormChange("genderStatus", newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Castrado"
                  placeholder="Selecione..."
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <HealingIcon sx={{ color: "action.active" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
            />
          </Grid>

          {/* Linha 5: Estado e Município */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={formData.state}
              onChange={(_, data) => onFormChange("state", data)}
              options={UFValueSet}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Estado"
                  placeholder="Selecione o estado"
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationIcon sx={{ color: "action.active" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Município"
              value={formData.city}
              onChange={(e) => onFormChange("city", e.target.value)}
              placeholder="Digite o município"
              disabled={!formData.state}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </ModernModal>
      {/* Dialog de ajuste da foto do pet */}
      <Dialog
        open={cropOpen}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") handleCancelCrop();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Ajustar foto do Pet</Typography>
            <IconButton
              onClick={handleCancelCrop}
              size="small"
              sx={{ color: "grey.600" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: cropSize,
                height: cropSize,
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid",
                borderColor: "#e0e0e0",
                bgcolor: "grey.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {cropImageSrc ? (
                <img
                  src={cropImageSrc || undefined}
                  alt="Pré-visualização de recorte"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    transform: `translate3d(${cropOffsetX}px, ${cropOffsetY}px, 0) scale(${cropScale})`,
                    transformOrigin: "center",
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Carregando imagem...
                </Typography>
              )}
            </Box>

            <Box sx={{ width: "100%", px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Zoom
              </Typography>
              <Slider
                min={0.5}
                max={3}
                step={0.01}
                value={cropScale}
                onChange={(_, val) => setCropScale(val as number)}
              />
            </Box>

            <Box sx={{ width: "100%", px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Posição vertical
              </Typography>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={cropOffsetY}
                onChange={(_, val) => setCropOffsetY(val as number)}
              />
            </Box>

            <Box sx={{ width: "100%", px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Posição horizontal
              </Typography>
              <Slider
                min={-100}
                max={100}
                step={1}
                value={cropOffsetX}
                onChange={(_, val) => setCropOffsetX(val as number)}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCrop}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmCrop}
            sx={{ bgcolor: "#00A884", "&:hover": { bgcolor: "#008c6e" } }}
            disabled={!cropImageSrc}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PetFormModal;
